/*
 * Gift with purchase
 *
 * Keeps the gift product in the cart only while the promo applies:
 * - adds 1 gift when the rest of the cart reaches the threshold,
 * - removes it when the total drops below, the cart holds nothing else,
 *   or the visitor is not eligible (B2B, promo off, gift sold out),
 * - keeps its quantity at 1,
 * - fills the gift progress row above the free shipping message (snippets/free-shipping.liquid).
 *
 * Config comes from window.theme.giftWithPurchase (snippets/head.liquid).
 * Runs on page load and after every cart update (theme:cart:change),
 * then asks the theme to re-render the cart (theme:cart:refresh).
 */

(function () {
  const config = window.theme && window.theme.giftWithPurchase;
  if (!config || !config.productId) return;

  const root = window.theme.routes.root || '/';
  const headers = {'Content-Type': 'application/json', Accept: 'application/json'};

  let isBusy = false;
  let isQueued = false;
  // Stop retrying the add after it failed once (e.g. the last bag was just taken)
  let addFailed = false;

  const post = (url, body) =>
    fetch(root + url, {method: 'POST', headers, body: JSON.stringify(body)}).then((response) => {
      if (!response.ok) throw new Error(`Gift with purchase: ${url} ${response.status}`);
      return response.json();
    });

  const setLineQuantity = (key, quantity) => post('cart/change.js', {id: key, quantity});

  function updateProgress(total, threshold) {
    const rows = document.querySelectorAll('[data-gift-progress]');
    if (!rows.length) return;

    const percent = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 100;
    const leftToSpend = window.theme.formatMoney(Math.max(threshold - total, 0), window.theme.moneyFormat).replace('.00', '');
    const isReached = total >= threshold;

    rows.forEach((row) => {
      const amount = row.querySelector('[data-gift-left-to-spend]');
      const progressBar = row.querySelector('[data-gift-progress-bar]');
      const hasSuccessMessage = !!row.querySelector('.free-shipping-gift__success-message');

      if (amount) amount.innerHTML = leftToSpend;
      if (progressBar) progressBar.value = percent;
      row.classList.toggle('is-success', isReached && hasSuccessMessage);
      row.hidden = false;
    });
  }

  async function sync() {
    if (isBusy) {
      isQueued = true;
      return;
    }

    isBusy = true;
    let hasChanged = false;

    try {
      const cart = await fetch(root + 'cart.js', {headers}).then((response) => response.json());
      const giftLines = cart.items.filter((item) => item.product_id === config.productId);
      const giftTotal = giftLines.reduce((total, item) => total + item.final_line_price, 0);
      const cartTotalWithoutGift = cart.total_price - giftTotal;
      const hasOtherItems = cart.items.length > giftLines.length;
      const threshold = Math.round(config.threshold * ((window.Shopify && Shopify.currency && Number(Shopify.currency.rate)) || 1));
      const qualifies = config.active && hasOtherItems && cartTotalWithoutGift >= threshold;

      updateProgress(cartTotalWithoutGift, threshold);

      if (qualifies && giftLines.length === 0) {
        if (!addFailed) {
          try {
            await post('cart/add.js', {items: [{id: config.variantId, quantity: 1}]});
            hasChanged = true;
          } catch (error) {
            addFailed = true;
            console.warn(error);
          }
        }
      } else if (!qualifies && giftLines.length) {
        for (const line of giftLines) {
          await setLineQuantity(line.key, 0);
        }
        hasChanged = true;
      } else if (qualifies && (giftLines.length > 1 || giftLines[0].quantity > 1)) {
        await setLineQuantity(giftLines[0].key, 1);
        for (const line of giftLines.slice(1)) {
          await setLineQuantity(line.key, 0);
        }
        hasChanged = true;
      }
    } catch (error) {
      console.warn(error);
    } finally {
      isBusy = false;
    }

    if (hasChanged) {
      // Re-render cart drawer / cart page; the theme fires theme:cart:change afterwards,
      // which re-runs sync() on an already consistent cart.
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }

    if (isQueued) {
      isQueued = false;
      sync();
    }
  }

  document.addEventListener('theme:cart:change', sync);
  sync();
})();
